import { t } from '@bia-auction/shared-i18n';
import type {
  BidKind,
  DepositState,
  LotState,
  PaymentState,
  PresentationStatus,
  Role,
  SaleState,
  SaleStyle,
} from '@bia-auction/shared-schema';

/** Enum → localized label, via the shared i18n bundle (AGENTS.md §Internationalization). */
export const presentationLabel = (s: PresentationStatus): string => t(`presentation.${s}`);
/**
 * The **raw** lifecycle state, for the sales-list status filter — which filters on
 * `sales.state`, not on the presentation label. The two are not interchangeable:
 * `presentationLabel` maps both PUBLISHED and a not-yet-started LIVE sale to "Hazırlanıyor",
 * so using it for filter options would offer the operator the same word twice.
 */
export const saleStateLabel = (s: SaleState): string => t(`saleState.${s}`);
export const lotStateLabel = (s: LotState): string => t(`lotState.${s}`);
export const saleStyleLabel = (s: SaleStyle): string => t(`style.${s}`);
export const depositStateLabel = (s: DepositState): string => t(`depositState.${s}`);
export const paymentStateLabel = (s: PaymentState): string => t(`paymentState.${s}`);
export const bidKindLabel = (s: BidKind): string => t(`bidKind.${s}`);
export const roleLabel = (r: Role): string => t(`role.${r}`);

/** The sale styles in display order (for the new-sale style picker). */
export const SALE_STYLES: SaleStyle[] = ['parallel', 'serial', 'serial-10', 'serial-with-cooldown'];

/**
 * Audit entity types the API writes today (`AuditEntry['entityType']` in
 * `apps/api/src/lib/audit.ts`), in reading order — most-looked-at first.
 */
export const AUDIT_ENTITY_TYPES = [
  'sale',
  'lot',
  'bid',
  'deposit',
  'payment',
  'user',
  'company',
  'vehicle',
  'submission',
  'api-key',
  'config',
] as const;

/** Audit actions the API writes today, grouped by area (auction → media → payment → system). */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'approve',
  'reject',
  'archive',
  'restore',
  'register',
  'membership-approved',
  'membership-rejected',
  'initiate',
  'grant-role',
  'bid-placed',
  'set-reserve',
  'take-control',
  'pause',
  'resume',
  're-add',
  'settlement-completed',
  'media-add',
  'media-update',
  'media-delete',
  'media-upload',
  'provision-conference',
  'teardown-conference',
  'teardown-conference-failed',
  'recording-capture-exhausted',
  'success',
  'fail',
  'revive',
  'duplicate-payment',
  'refund',
  'refund-ambiguous',
  'refund-declined',
  'refund-topbidder-conflict',
  'void',
  'credit',
  'reconcile-ambiguous',
  'reconcile-attempt-failed',
  'reconcile-charged-pending',
  'reconcile-confirmed',
  'reconcile-returned',
  'reconcile-reverted',
  'reconcile-mark-failed',
  'push-subscription-set',
  'push-subscription-reassigned',
  'push-subscription-cleared',
  'push-channel-cleared',
  'fcm-token-set',
  'fcm-token-reassigned',
  'fcm-token-cleared',
  'locale-updated',
  'retention-anonymize-batch',
  'retention-anonymize-consent-batch',
  'retention-purge-batch',
] as const;

const KNOWN_ENTITY_TYPES: ReadonlySet<string> = new Set(AUDIT_ENTITY_TYPES);
const KNOWN_ACTIONS: ReadonlySet<string> = new Set(AUDIT_ACTIONS);

/**
 * Localize an `audit_log.entity_type` / `.action`. Both columns are **free text** — the audit
 * trail is history and outlives any enum, so a value written by an older build (or one added to
 * the API without a label here) falls back to the raw slug rather than rendering blank. The
 * filter still sends the raw slug either way, so localizing the display never breaks a
 * deep-linked `/audit?entityType=…&action=…`.
 */
export const auditEntityTypeLabel = (e: string): string =>
  KNOWN_ENTITY_TYPES.has(e) ? t(`audit.entity.${e as (typeof AUDIT_ENTITY_TYPES)[number]}`) : e;
export const auditActionLabel = (a: string): string =>
  KNOWN_ACTIONS.has(a) ? t(`audit.action.${a as (typeof AUDIT_ACTIONS)[number]}`) : a;
